import React, { useState, useEffect } from "react";
import { createOrder, getUserDetail, uploadFile } from "../apis/auth";

// Reusable Input component
const Input = ({ label, value, onChange, name }) => (
  <div className="flex flex-col gap-1">
    <label className="text-sm text-gray-600">{label}</label>
    {name === "description" ? (
      <textarea
        placeholder="Enter"
        className="border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 h-28 resize-none"
        value={value}
        onChange={(e) => onChange(name, e.target.value)}
      />
    ) : (
      <input
        type="text"
        placeholder="Enter"
        className="border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        value={value}
        onChange={(e) => onChange(name, e.target.value)}
      />
    )}
  </div>
);

const BookingForm = ({ onCancel }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [savedData, setSavedData] = useState({
    shipperDetails: {},
    consigneeDetails: {},
    notify: {},
    notify2: {}
  });

  const [formData, setFormData] = useState({
    bookingType: "",
    container: "",
    shipperDetails: {
      name: "",
      address: "",
      city: "",
      country: "",
      RUC: "",
      contactNumber: "",
      notes: "",
    },
    notify: {
      name: "",
      address: "",
      zipCode: "",
      contactNumber: "",
      city: "",
      country: "",
      contactName: "",
      contactEmail: "",
      EORI: "",
    },
    notify2: {
      name: "",
      address: "",
      zipCode: "",
      contactNumber: "",
      city: "",
      country: "",
      contactName: "",
      contactEmail: "",
      EORI: "",
    },
    consigneeDetails: {
      name: "",
      address: "",
      city: "",
      country: "",
      RUC: "",
      contactNumber: "",
      notes: "",
      EORI: "",
    },
    shipmentDetails: {
      oceanVessel: "",
      callNumber: "",
      POL: "",
      POD: "",
      bookingNumber: "",
      marksAndNo: "", // Added new field
    },
    goodsDetails: [
      {
        containerNumber: "",
        DAE: "",
        brand: "",
        TARE: "",
        TariffHeading: "",
        totalWeight: "",
        totalGross: "",
        temperature: "",
        ventilation: "",
        shippingSeal: "",
        securitySeal1: "",
        securitySeal2: "",
        attachment: "",
        VGM: "",
        description: "",
      },
    ],
    // Single attachment for all containers when booking type is "Container"
    containerAttachment: "",
  });

  // Load saved data from localStorage on component mount
  useEffect(() => {
    const loadSavedData = () => {
      const saved = localStorage.getItem('logisticsAutofillData');
      if (saved) {
        setSavedData(JSON.parse(saved));
      }
    };
    loadSavedData();
  }, []);

  // Save data to localStorage whenever form data changes
  const saveToLocalStorage = (section, data) => {
    const currentSaved = JSON.parse(localStorage.getItem('logisticsAutofillData') || '{}');
    currentSaved[section] = data;
    localStorage.setItem('logisticsAutofillData', JSON.stringify(currentSaved));
    setSavedData(currentSaved);
  };

  // Autofill function
  const autofillSection = (section) => {
    if (savedData[section]) {
      setFormData(prev => ({
        ...prev,
        [section]: { ...prev[section], ...savedData[section] }
      }));
    }
  };

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const userDetails = await getUserDetail();
        setFormData((prev) => ({
          ...prev,
          shipperDetails: {
            name: userDetails.data.name || "",
            address: userDetails.data.address || "",
            city: userDetails.data.city || "",
            country: userDetails.data.country || "",
            RUC: userDetails.data.RUC || "",
            contactNumber: userDetails.data.phone || "",
            notes: userDetails.data.notes || "",
          },
        }));
      } catch (err) {
        console.error("Error fetching user details:", err);
      }
    };
    fetchUserDetails();
  }, []);

  const steps = ["Booking Details", "Shipment Details", "Goods Description"];

  const handleInputChange = (section, field, value) => {
    setFormData((prev) => {
      const updated = section
        ? { ...prev, [section]: { ...prev[section], [field]: value } }
        : { ...prev, [field]: value };
      
      // Save to localStorage for autofill
      if (section && ['shipperDetails', 'consigneeDetails', 'notify', 'notify2'].includes(section)) {
        saveToLocalStorage(section, updated[section]);
      }
      
      return updated;
    });
  };

  const handleGoodsChange = (index, field, value) => {
    setFormData((prev) => {
      const updatedGoods = [...prev.goodsDetails];
      updatedGoods[index][field] = value;
      return {
        ...prev,
        goodsDetails: updatedGoods,
      };
    });
  };

  // Add new container
  const addContainer = () => {
    setFormData((prev) => ({
      ...prev,
      goodsDetails: [
        ...prev.goodsDetails,
        {
          containerNumber: "",
          DAE: "",
          brand: "",
          TARE: "",
          TariffHeading: "",
          totalWeight: "",
          totalGross: "",
          temperature: "",
          ventilation: "",
          shippingSeal: "",
          securitySeal1: "",
          securitySeal2: "",
          attachment: "",
          VGM: "",
          description: "",
        },
      ],
    }));
  };

  // Remove container
  const removeContainer = (index) => {
    if (formData.goodsDetails.length > 1) {
      setFormData((prev) => ({
        ...prev,
        goodsDetails: prev.goodsDetails.filter((_, i) => i !== index),
      }));
    }
  };

  // Handle single attachment for all containers
  const handleContainerAttachmentUpload = async (file) => {
    try {
      const formDataFile = new FormData();
      formDataFile.append("file", file);

      const response = await uploadFile(formDataFile);
      console.log("File uploaded successfully:", response.data.data[0]);
      if (response.data.success && response.data.data[0]?.url) {
        // Save the URL in containerAttachment
        setFormData((prev) => ({
          ...prev,
          containerAttachment: response.data.data[0].url,
        }));
      } else {
        alert("Failed to upload file");
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      alert("Error uploading file");
    }
  };

  // Handle individual attachment for Break Bull
  const handleGoodsFileUpload = async (index, file) => {
    try {
      const formDataFile = new FormData();
      formDataFile.append("file", file);

      const response = await uploadFile(formDataFile);
      console.log("File uploaded successfully:", response.data.data[0]);
      if (response.data.success && response.data.data[0]?.url) {
        // Save the URL in the goodsDetails
        handleGoodsChange(index, "attachment", response.data.data[0].url);
      } else {
        alert("Failed to upload file");
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      alert("Error uploading file");
    }
  };

  const handleSubmit = async () => {
    try {
      // Build the final payload
      const payload = {
        bookingType: formData.bookingType,
        container: formData.container,
        shipperDetails: formData.shipperDetails,
        notify: formData.notify,
        notify2: formData.notify2,
        consigneeDetails: formData.consigneeDetails,
        shipmentDetails: formData.shipmentDetails,
        goodsDetails: formData.goodsDetails,
        // Include containerAttachment for Container booking type
        ...(formData.bookingType === "Container" && {
          containerAttachment: formData.containerAttachment,
        }),
      };
  
      const response = await createOrder(payload);
      console.log("Order created successfully:", response);
      alert("Order created successfully!");
      onCancel();
    } catch (err) {
      console.error("Error creating order:", err);
      alert("Failed to create order. Please try again.");
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold">Create New Order</h2>
            <button
              onClick={onCancel}
              className="text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center justify-center mb-8">
            {steps.map((step, index) => (
              <div key={index} className="flex items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    index <= activeStep
                      ? "bg-blue-600 text-white"
                      : "bg-gray-200 text-gray-600"
                  }`}
                >
                  {index + 1}
                </div>
                <span className="ml-2 text-sm font-medium text-gray-600">
                  {step}
                </span>
                {index < steps.length - 1 && (
                  <div className="w-16 h-px bg-gray-300 mx-4"></div>
                )}
              </div>
            ))}
          </div>

          {/* Step 1 */}
          {activeStep === 0 && (
            <div className="text-gray-500">
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div className="flex flex-col gap-1">
                  <label className="text-sm text-gray-600">Booking Type</label>
                  <select
                    className="border rounded px-3 py-2 text-sm"
                    value={formData.bookingType}
                    onChange={(e) =>
                      handleInputChange(null, "bookingType", e.target.value)
                    }
                  >
                    <option value="">Select</option>
                    <option value="Container">Container</option>
                    <option value="Break Bull">Break Bull</option>
                  </select>
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-sm text-gray-600">Cargo Type</label>
                  <select
                    className="border rounded px-3 py-2 text-sm"
                    value={formData.container}
                    onChange={(e) =>
                      handleInputChange(null, "container", e.target.value)
                    }
                  >
                    <option value="">Select</option>
                    <option value="Organic">Organic</option>
                    <option value="Conventional">Conventional</option>
                    <option value="Convencional/Organica">Convencional/Organica</option>
                  </select>
                </div>
              </div>

              {/* Shipper Details */}
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-semibold">Shipper Details:</h3>
                    {savedData.shipperDetails && Object.keys(savedData.shipperDetails).length > 0 && (
                      <button
                        onClick={() => autofillSection('shipperDetails')}
                        className="text-xs text-blue-600 border border-blue-600 px-2 py-1 rounded hover:bg-blue-50"
                      >
                        Autofill
                      </button>
                    )}
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4 mb-4">
                    {Object.keys(formData.shipperDetails).map((key) => (
                      <Input
                        key={key}
                        label={key.charAt(0).toUpperCase() + key.slice(1)}
                        value={formData.shipperDetails[key]}
                        onChange={(field, value) =>
                          handleInputChange("shipperDetails", key, value)
                        }
                        name={key}
                      />
                    ))}
                  </div>
                </div>

                {/* Consignee Details */}
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-semibold">Consignee Details:</h3>
                    {savedData.consigneeDetails && Object.keys(savedData.consigneeDetails).length > 0 && (
                      <button
                        onClick={() => autofillSection('consigneeDetails')}
                        className="text-xs text-blue-600 border border-blue-600 px-2 py-1 rounded hover:bg-blue-50"
                      >
                        Autofill
                      </button>
                    )}
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4 mb-4">
                    {Object.keys(formData.consigneeDetails).map((key) => (
                      <Input
                        key={key}
                        label={key.charAt(0).toUpperCase() + key.slice(1)}
                        value={formData.consigneeDetails[key]}
                        onChange={(field, value) =>
                          handleInputChange("consigneeDetails", key, value)
                        }
                        name={key}
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* Notify Details */}
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-semibold">Notify Details:</h3>
                    {savedData.notify && Object.keys(savedData.notify).length > 0 && (
                      <button
                        onClick={() => autofillSection('notify')}
                        className="text-xs text-blue-600 border border-blue-600 px-2 py-1 rounded hover:bg-blue-50"
                      >
                        Autofill
                      </button>
                    )}
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4 mb-4">
                    {Object.keys(formData.notify).map((key) => (
                      <Input
                        key={key}
                        label={key.charAt(0).toUpperCase() + key.slice(1)}
                        value={formData.notify[key]}
                        onChange={(field, value) =>
                          handleInputChange("notify", key, value)
                        }
                        name={key}
                      />
                    ))}
                  </div>
                </div>

                {/* Notify 2 Details */}
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-semibold">Notify 2 Details:</h3>
                    {savedData.notify2 && Object.keys(savedData.notify2).length > 0 && (
                      <button
                        onClick={() => autofillSection('notify2')}
                        className="text-xs text-blue-600 border border-blue-600 px-2 py-1 rounded hover:bg-blue-50"
                      >
                        Autofill
                      </button>
                    )}
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4 mb-4">
                    {Object.keys(formData.notify2).map((key) => (
                      <Input
                        key={key}
                        label={key.charAt(0).toUpperCase() + key.slice(1)}
                        value={formData.notify2[key]}
                        onChange={(field, value) =>
                          handleInputChange("notify2", key, value)
                        }
                        name={key}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2 */}
          {activeStep === 1 && (
            <div className="text-gray-500">
              <h3 className="font-semibold text-lg mb-6">Shipment Details:</h3>
              <div className="grid md:grid-cols-2 gap-6">
                {Object.keys(formData.shipmentDetails).map((key) => {
                  // Only show "Marks and No." field for Break Bull booking type
                  if (key === "marksAndNo" && formData.bookingType !== "Break Bull") {
                    return null;
                  }
                  
                  return (
                    <Input
                      key={key}
                      label={key === "marksAndNo" ? "Marks and No." : key.charAt(0).toUpperCase() + key.slice(1)}
                      value={formData.shipmentDetails[key]}
                      onChange={(field, value) =>
                        handleInputChange("shipmentDetails", key, value)
                      }
                      name={key}
                    />
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 3 */}
          {activeStep === 2 && (
            <div className="text-gray-500">
              <div className="bg-white rounded-lg text-sm max-w-3xl">
                <h3 className="font-semibold text-lg mb-6">Goods Details:</h3>
                {formData.bookingType === "Container" && (
                  <>
                    {formData.goodsDetails.map((container, index) => (
                      <div key={index} className="mb-6">
                        <div className="border border-gray-300 p-4 rounded">
                          <div className="flex justify-between items-center mb-4">
                            <h4 className="font-medium text-indigo-600">
                              Container {index + 1}
                            </h4>
                            {formData.goodsDetails.length > 1 && (
                              <button
                                onClick={() => removeContainer(index)}
                                className="text-red-600 hover:text-red-800 text-sm font-medium px-2 py-1 border border-red-600 rounded hover:bg-red-50 transition-colors"
                              >
                                Remove Container
                              </button>
                            )}
                          </div>
                          <div className="grid md:grid-cols-2 gap-6">
                            {Object.keys(container).map((key) =>
                              key !== "attachment" ? (
                                <Input
                                  key={key}
                                  label={key.charAt(0).toUpperCase() + key.slice(1)}
                                  value={container[key]}
                                  onChange={(field, value) =>
                                    handleGoodsChange(index, key, value)
                                  }
                                  name={key}
                                />
                              ) : null
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    <button
                      onClick={addContainer}
                      className="text-sm text-blue-600 border border-blue-600 px-4 py-1 rounded hover:bg-blue-50 mb-6"
                    >
                      + Add Another Container
                    </button>

                    {/* Single attachment field for all containers */}
                    <div className="p-4 bg-gray-50 rounded border-l-4 border-blue-500">
                      <div className="flex flex-col gap-2">
                        <label className="text-sm font-medium text-gray-700">
                          Attachment for All Containers
                        </label>
                        <input
                          type="file"
                          className="text-sm file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                          onChange={(e) =>
                            handleContainerAttachmentUpload(e.target.files[0])
                          }
                        />
                        {formData.containerAttachment && (
                          <p className="text-xs text-green-600">
                            ✓ File uploaded successfully for all containers
                          </p>
                        )}
                      </div>
                    </div>
                  </>
                )}

                {formData.bookingType === "Break Bull" && (
                  <>
                    {formData.goodsDetails.map((goods, index) => (
                      <div key={index} className="mb-6">
                        <div className="border border-gray-300 p-4 rounded">
                          <div className="flex justify-between items-center mb-4">
                            <h4 className="font-medium text-indigo-600">
                              Goods Row {index + 1}
                            </h4>
                            {formData.goodsDetails.length > 1 && (
                              <button
                                onClick={() => removeContainer(index)}
                                className="text-red-600 hover:text-red-800 text-sm font-medium px-2 py-1 border border-red-600 rounded hover:bg-red-50 transition-colors"
                              >
                                Remove Row
                              </button>
                            )}
                          </div>
                          <div className="grid md:grid-cols-2 gap-6">
                            {["DAE", "brand", "description", "TariffHeading", "totalWeight", "totalGross"].map(
                              (field) => (
                                <Input
                                  key={field}
                                  label={field.charAt(0).toUpperCase() + field.slice(1)}
                                  value={goods[field] || ""}
                                  onChange={(fieldName, value) =>
                                    handleGoodsChange(index, field, value)
                                  }
                                  name={field}
                                />
                              )
                            )}
                          </div>
                          
                          {/* Show Marks and No. field only for Break Bull */}
                          {formData.bookingType === "Break Bull" && formData.shipmentDetails.marksAndNo && (
                            <div className="mt-4 p-3 bg-blue-50 rounded">
                              <label className="text-sm font-medium text-gray-700">Marks and No.:</label>
                              <p className="text-sm text-gray-600">{formData.shipmentDetails.marksAndNo}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                    
                    <button
                      onClick={addContainer}
                      className="text-sm text-blue-600 border border-blue-600 px-4 py-1 rounded hover:bg-blue-50 mb-6"
                    >
                      + Add Another Row
                    </button>
                    
                    {/* Attachment field for Break Bull */}
                    <div className="p-4 bg-gray-50 rounded border-l-4 border-blue-500">
                      <div className="flex flex-col gap-2">
                        <label className="text-sm font-medium text-gray-700">
                          Attachment
                        </label>
                        <input
                          type="file"
                          className="text-sm file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                          onChange={(e) =>
                            handleGoodsFileUpload(0, e.target.files[0])
                          }
                        />
                        {formData.goodsDetails[0].attachment && (
                          <p className="text-xs text-green-600">
                            ✓ File uploaded successfully
                          </p>
                        )}
                      </div>
                    </div>
                  </>
                )}

                {!formData.bookingType && (
                  <p className="text-red-500">
                    Please select a Booking Type in Step 1.
                  </p>
                )}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-4 mt-8">
            <button
              onClick={onCancel}
              className="border border-gray-300 px-6 py-2 rounded text-gray-700 hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              onClick={() =>
                activeStep === steps.length - 1
                  ? handleSubmit()
                  : setActiveStep((prev) => Math.min(prev + 1, steps.length - 1))
              }
              className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"
            >
              {activeStep === steps.length - 1 ? "Submit" : "Next"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingForm;
